import { forgetPlayer } from './analytics.ts';

/**
 * Two modes. `dev` (docker compose only): a handle typed on the sign-in page becomes `dev:<handle>`.
 * `cognito`: managed login via OAuth authorisation code + PKCE; tokens kept in localStorage.
 */
export type AuthConfig = { mode: 'dev' } | { mode: 'cognito'; domain: string; clientId: string };

interface Tokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

const KEY = 'wyn.auth';
const VERIFIER = 'wyn.pkce';

const safe = {
  get: (k: string) => {
    try { return localStorage.getItem(k); } catch { return null; }
  },
  set: (k: string, v: string) => {
    try { localStorage.setItem(k, v); } catch { /* private mode */ }
  },
  del: (k: string) => {
    try { localStorage.removeItem(k); } catch { /* private mode */ }
  },
};

const b64url = (bytes: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export interface Auth {
  mode: AuthConfig['mode'];
  isSignedIn(): boolean;
  getToken(): Promise<string | null>;
  /** dev: pass a handle. cognito: redirects away. */
  signIn(handle?: string): Promise<void>;
  completeSignIn(params: URLSearchParams): Promise<void>;
  /** Forgets the tokens and leaves for the landing page. cognito: by way of its logout, which ends the managed-login session too. */
  signOut(): void;
}

export function createAuth(config: AuthConfig): Auth {
  const read = (): Tokens | null => {
    const raw = safe.get(KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  };
  const write = (t: Tokens | null) => (t ? safe.set(KEY, JSON.stringify(t)) : safe.del(KEY));
  const redirectUri = () => `${location.origin}/auth/callback`;

  async function tokenRequest(body: Record<string, string>): Promise<Tokens> {
    if (config.mode !== 'cognito') throw new Error('not cognito');
    const res = await fetch(`${config.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: config.clientId, ...body }),
    });
    if (!res.ok) throw new Error('Sign-in failed. Please try again.');
    const j = (await res.json()) as { access_token: string; refresh_token?: string; expires_in: number };
    return { accessToken: j.access_token, refreshToken: j.refresh_token ?? body.refresh_token, expiresAt: Date.now() + j.expires_in * 1000 };
  }

  return {
    mode: config.mode,
    isSignedIn: () => read() !== null,
    async getToken() {
      const t = read();
      if (!t) return null;
      if (config.mode === 'dev' || t.expiresAt - 60_000 > Date.now()) return t.accessToken;
      if (!t.refreshToken) return write(null), null;
      try {
        const fresh = await tokenRequest({ grant_type: 'refresh_token', refresh_token: t.refreshToken });
        write(fresh);
        return fresh.accessToken;
      } catch {
        write(null);
        return null;
      }
    },
    async signIn(handle) {
      if (config.mode === 'dev') {
        const h = (handle ?? '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
        if (!h) throw new Error('Enter a handle.');
        write({ accessToken: `dev:${h}`, expiresAt: Number.MAX_SAFE_INTEGER });
        return;
      }
      const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
      const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
      const state = b64url(crypto.getRandomValues(new Uint8Array(16)));
      safe.set(VERIFIER, JSON.stringify({ verifier, state }));
      const q = new URLSearchParams({
        response_type: 'code',
        client_id: config.clientId,
        redirect_uri: redirectUri(),
        scope: 'openid email profile',
        code_challenge_method: 'S256',
        code_challenge: challenge,
        state,
      });
      location.assign(`${config.domain}/oauth2/authorize?${q}`);
    },
    async completeSignIn(params) {
      const saved = JSON.parse(safe.get(VERIFIER) ?? 'null') as { verifier: string; state: string } | null;
      safe.del(VERIFIER);
      const code = params.get('code');
      if (!saved || !code || params.get('state') !== saved.state) throw new Error('Sign-in could not be verified. Please try again.');
      write(await tokenRequest({ grant_type: 'authorization_code', code, code_verifier: saved.verifier, redirect_uri: redirectUri() }));
    },
    signOut() {
      write(null);
      forgetPlayer();
      // Navigate once only: a second `location.assign` would cancel the trip to Cognito's logout, and its session would sign the player straight back in.
      if (config.mode === 'cognito') {
        location.assign(`${config.domain}/logout?${new URLSearchParams({ client_id: config.clientId, logout_uri: `${location.origin}/` })}`);
      } else location.assign('/');
    },
  };
}
